import { useParams } from 'react-router-dom';
import { DashboardMap } from 'src/components/Maps/dashboardMap';
import { JobDetails } from 'src/components/Orders/JobDetails/jobDetails';
import {
  useGetCurrentTripMutation,
  useGetOrderEventsByIdQuery,
  useGetOrdersByIdQuery,
  useGetPresetsQuery,
  usePatchOrderCompletedByIdMutation,
  useTrackOrdersByIdQuery,
} from 'src/services/orders/orders';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COMPLETED, IN_TRANSIT } from 'src/constants/statusConstants';
import { subscribeTracker } from 'src/services/avlData/avlData';
import moment from 'moment';
import { VehicleLog } from 'src/components/Orders/VehicleLog/vehicleLog';
import {
  Modal,
  ModalBody,
  ModalContent,
  Tab,
  Tabs,
} from '@heroui/react';
import { t } from 'i18next';
import CameraVideos from 'src/components/Orders/HowenVideos/cameraVideos';
import { getUserType } from 'src/utils/user/user';
import i18n from 'src/i18n';
import { AnimatePresence, motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaChevronUp } from 'src/icons';
import { FaArrowLeft } from 'react-icons/fa6';

export interface IOrderDetailsProps { }

export function OrderDetails(props: IOrderDetailsProps) {
  const { id } = useParams();
  const navigate = useNavigate();

  // State for the side panel (JobDetails)
  const [showSidePanel, setShowSidePanel] = useState<boolean>(true);

  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [avlData, setAvlData] = useState<any>(null);
  const [eventData, setEventData] = useState<any>([]);
  const [activeTab, setActiveTab] = useState('1');
  const [showCameras, setShowCameras] = useState<boolean>(false);

  const [tripHistory, tripHistoryResult] = useGetCurrentTripMutation();
  const [trigger] = usePatchOrderCompletedByIdMutation();

  const {
    data: Order,
    isLoading,
    error,
    refetch,
  } = useGetOrdersByIdQuery(id || '', {
    skip: !id,
  });

  const isInTransit = Order?.status === 'in_transit';

  const { data: Events } = useGetOrderEventsByIdQuery(id || '', {
    skip: !id || !isInTransit,
    pollingInterval: isInTransit ? 30000 : 0,
  });
  const { data: Tracker } = useTrackOrdersByIdQuery(id || '', {
    skip: !id || !isInTransit,
    pollingInterval: isInTransit ? 10000 : 0,
  });

  const { data: presetData } = useGetPresetsQuery();
  const [assignedRoute, setAssignedRoute] = useState<boolean>(false);
  const lockId = Order?.lock;
  const [historyRoute, setHistoryRoute] = useState<any>([]);
  const [trackerData, setTrackerData] = useState<any>([]);
  const isRequestPendingRef = useRef(false);
  const [gpsPositions, setGpsPositions] = useState<any>([]);
  const [devicePosition, setDevicePosition] = useState<any>(undefined);
  const [howenToken, setHowenToken] = useState('');
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const routeOrigin = {
    latitude: 25.188163682053897,
    longitude: 55.2989263100694,
  };
  const routeDestination = {
    latitude: 25.187040537554285,
    longitude: 55.29678837311032,
  };

  const userType = getUserType();

  const deviceId = Order?.vehicle?.imei;

  useEffect(() => {
    if (!Order) return;
    const Route = Order?.route?.coordinates?.map((coordinates: any) => ({
      lat: coordinates[1],
      lng: coordinates[0],
    }));
    getTripHistory();
    setAssignedRoute(Route);
  }, [Order]);

  useEffect(() => {
    if (!Order || !Order?.vehicle?.trackerid) return;
    const maybeSetInterval = () => {
      if (Order.status === IN_TRANSIT) {
        const intervalId = setInterval(() => {
          getTripHistory();
        }, 10000);
        return () => clearInterval(intervalId);
      }
    };
    const cleanup = maybeSetInterval();
    return cleanup;
  }, [Order]);

  const getTripHistory = () => {
    if (!Order || !Order?.vehicle?.trackerid || isRequestPendingRef.current)
      return;
    isRequestPendingRef.current = true;
    let to = moment().valueOf();
    let from = moment().valueOf();

    if (Order.status === COMPLETED) {
      to = moment(Order.completedAt).valueOf();
      from = moment(Order.startedAt || Order.createdAt).valueOf();
    } else if (Order.status === IN_TRANSIT) {
      from = moment(Order.startedAt || Order.createdAt).valueOf();
    }

    const payload = {
      trackerid: Order?.vehicle?.trackerid,
      from: from,
      to: to,
    };

    tripHistory(payload)
      .unwrap()
      .then((response: any) => {
        const Route = response?.map((coordinates: any) => ({
          lat: coordinates.latitude,
          lng: coordinates.longitude,
        }));
        setHistoryRoute(Route);
        setTrackerData(response);
      })
      .finally(() => {
        isRequestPendingRef.current = false;
      });
  };

  const handleSocketData = (data: any) => {
    if (
      data.location.longitude_degrees !== 0 ||
      data.location.latitude_degrees !== 0
    ) {
      setAvlData(data.location);
    }
  };

  const handleEventsData = (data: any) => {
    setEventData((prevEventData: any) => [...prevEventData, data]);
  };

  useEffect(() => {
    let unsubscribe: () => void;

    if (lockId) {
      unsubscribe = subscribeTracker(lockId, handleSocketData);
      // subscribeEvents(lockId, handleEventsData);  // If needed.
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [lockId]);

  const handleRefetch = () => {
    refetch();
  };

  const checkForOrderCompletion = useCallback(() => {
    if (
      Order &&
      !isCompleted &&
      Tracker &&
      arePointsNear(
        { lat: Tracker.latitude, lng: Tracker.longitude },
        { lat: routeDestination.latitude, lng: routeDestination.longitude }
      )
    ) {
      setIsCompleted(true);
      trigger(Order._id);
      refetch();
    }
  }, [Tracker, refetch, trigger, Order, isCompleted, routeDestination]);

  const arePointsNear = (checkPoint: any, centerPoint: any, km = 0.1) => {
    const ky = 40000 / 360;
    const kx = Math.cos((Math.PI * centerPoint.lat) / 180.0) * ky;
    const dx = Math.abs(centerPoint.lng - checkPoint.lng) * kx;
    const dy = Math.abs(centerPoint.lat - checkPoint.lat) * ky;
    return Math.sqrt(dx * dx + dy * dy) <= km;
  };

  const completeOrder = () => {
    if (!Order) return;
    if (Order.status === IN_TRANSIT) {
      setIsCompleted(true);
    }
  };

  // Token for Howen cameras
  useEffect(() => {
    const fetchTokenAndData = async () => {
      if (Order) {
        try {
          const tokenPid = await loginAndGetToken();
          if (tokenPid) {
            const beginTime = moment(Order.startedAt).format(
              'YYYY-MM-DD HH:mm:ss'
            );
            const endTime = moment()
              .add(4, 'hours')
              .format('YYYY-MM-DD HH:mm:ss');
            const historicalPositions = await getHistoricalData(
              tokenPid.token,
              beginTime,
              endTime,
              deviceId
            );
            setGpsPositions(historicalPositions);
            if (historicalPositions.length > 0) {
              setDevicePosition(
                historicalPositions[historicalPositions.length - 1]
              );
            }
          }
        } catch (error) {
          console.error('Error fetching token or historical data:', error);
        }
      }
    };

    fetchTokenAndData();
  }, [Order]);

  const loginAndGetToken = async () => {
    try {
      const data = {
        username: 'Emcode',
        password: 'Demo1234',
      };
      const response = await fetch(
        `https://vss.howentech.com/vss/user/login.action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      if (result) {
        setHowenToken(result.data.token);
        return { token: result.data.token, pid: result.data.pid };
      } else {
        throw new Error('Failed to get token');
      }
    } catch (error) {
      console.error('Login Error:', error);
    }
  };

  const getHistoricalData = async (
    token: string,
    beginTime: string,
    endTime: string,
    deviceID: string
  ) => {
    try {
      const data = { deviceID, beginTime, endTime, token };
      const response = await fetch(
        `https://vss.howentech.com/vss/track/getTrackAndAlarm.action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      if (result.data) {
        return result.data
          .map((item: any) => ({
            lat: parseFloat(item.location.latitude),
            lng: parseFloat(item.location.longitude),
          }))
          .filter((pos: any) => pos.lat !== 0 && pos.lng !== 0);
      }
      return [];
    } catch (error) {
      console.error('Error fetching historical data:', error);
      return [];
    }
  };

  // Real-time data (websocket) if in transit
  useEffect(() => {
    if (Order?.status === IN_TRANSIT) {
      let ws: WebSocket;

      const initializeGpsData = async () => {
        const tokenPid = await loginAndGetToken();
        if (tokenPid) {
          ws = new WebSocket('wss://vss.howentech.com:36301/wss');

          ws.onopen = () => {
            ws.send(
              JSON.stringify({
                action: '80000',
                payload: {
                  username: 'Emcode',
                  token: tokenPid.token,
                  pid: tokenPid.pid,
                },
              })
            );
          };

          ws.onmessage = (event) => {
            const result = JSON.parse(event.data);
            if (
              result.action === '80003' &&
              result.payload.deviceID === deviceId &&
              result.payload &&
              result.payload.location
            ) {
              const newPosition = {
                lat: parseFloat(result.payload.location.latitude),
                lng: parseFloat(result.payload.location.longitude),
              };


              if (
                !isNaN(newPosition.lat) &&
                !isNaN(newPosition.lng) &&
                newPosition.lat !== 0 &&
                newPosition.lng !== 0
              ) {
                setGpsPositions((prevPositions: any) => {
                  const lastPos = prevPositions[prevPositions.length - 1];
                  if (
                    !lastPos ||
                    lastPos.lat !== newPosition.lat ||
                    lastPos.lng !== newPosition.lng
                  ) {
                    return [...prevPositions, newPosition];
                  }
                  return prevPositions;
                });

                setDevicePosition(newPosition);
              } else {
                console.error(
                  'Invalid coordinates received or coordinates are 0:',
                  newPosition
                );
              }
            }
          };

          ws.onerror = (error) => {
            console.error('WebSocket Error:', error);
          };

          ws.onclose = () => {
          };
        }
      };

      initializeGpsData();

      return () => {
        if (ws) {
          ws.close();
        }
      };
    }
  }, [Order]);

  // const handleVideoClick = () => {
  //   const video = videoRef.current;
  //   if (video) {
  //     setIsVideoOpen(true);

  //     // Attempt to enter fullscreen
  //     const requestFullscreen =
  //       video.requestFullscreen ||
  //       (video as any).webkitRequestFullscreen ||
  //       (video as any).mozRequestFullScreen ||
  //       (video as any).msRequestFullscreen;

  //     if (requestFullscreen) {
  //       requestFullscreen.call(video);
  //     }

  //     video
  //       .play()
  //       .catch((err) => console.error('Video play failed:', err));
  //   }
  // };

  useEffect(() => {
    if (isVideoOpen && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.warn('Autoplay prevented:', err);
        });
      }
    }
  }, [isVideoOpen]);

  const handleVideoClick = () => {
    // Show Camera Video
    // setIsVideoOpen(true);
    setShowCameras(true);
  };

  return (
    <div>
      {isLoading && <div>Loading...</div>}
      {error && <div>Error fetching order details</div>}

      {Order && (
        <div className="flex w-screen h-screen overflow-hidden">
          {/* Conditional rendering of LEFT PANEL (Job Details) */}
          {/* @ts-ignore */}
          <AnimatePresence initial={false}>
            {showSidePanel && (
              <motion.div
                key="side-panel"
                initial={{
                  x: i18n.language === 'ar' ? '100%' : '-100%',
                  opacity: 0,
                  width: 0,
                }}
                animate={{
                  x: 0,
                  opacity: 1,
                  width: '25%',
                }}
                exit={{
                  x: i18n.language === 'ar' ? '100%' : '-100%',
                  opacity: 0,
                  width: 0,
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="h-full flex flex-col border-x z-40 overflow-hidden" style={{ backgroundColor: "var(--bg-surface)", color: "var(--text-main)", borderColor: "var(--border-light)" }}
              >
                {/* Header row */}
                <div className="p-4 flex items-center justify-between" style={{ backgroundColor: "var(--bg-surface)" }}>
                  <div className="flex items-center space-x-3">
                    <FaArrowLeft
                      className={`cursor-pointer text-xl ${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'}`}
                      onClick={() => navigate('/orders')}
                      style={{ color: "var(--text-main)" }}
                    />
                    <h3 className="font-bold text-md">
                      {Order?.containerNumber}
                    </h3>
                  </div>
                </div>

                {/* Tabs */}
                <Tabs
                  aria-label="Order Details"
                  classNames={{
                    base: 'flex flex-row mb-4',
                    cursor: 'bg-[var(--color-primary)] w-full',
                    tabList:
                      'w-full relative rounded-none p-0 border-b-2 border-[var(--border-light)]',
                    tab: 'font-semibold w-full h-14 text-[16px]',
                    tabContent:
                      'text-[var(--text-muted)] group-data-[selected=true]:text-[var(--text-main)] transition-all duration-300',
                  }}
                  color="primary"
                  variant="underlined"
                >
                  <Tab
                    key="1"
                    title={
                      <div className="flex items-center space-x-2">
                        <span className="text-md font-semibold">
                          {t('order_details.job_details')}
                        </span>
                      </div>
                    }
                  >
                    <div className="h-[calc(100vh-5rem)] overflow-y-auto pb-36">
                      <JobDetails
                        Order={Order}
                        presetData={presetData}
                        refetchOrder={handleRefetch}
                      />
                    </div>
                  </Tab>

                  {Order.status === IN_TRANSIT && (
                    <Tab
                      key="2"
                      title={
                        <div className="flex items-center space-x-2">
                          <span className="text-md font-semibold">
                            {t('order_details.events')}
                          </span>
                        </div>
                      }
                    >
                      <div className="h-[calc(100vh-5rem)] overflow-y-auto p-4">
                        <VehicleLog Order={Order} />
                      </div>
                    </Tab>
                  )}
                </Tabs>
              </motion.div>
            )}
          </AnimatePresence>

          {/* RIGHT PANEL: Cameras + Map */}
          <div className="flex-1 relative bg-transparent">
            <div
              className={`flex justify-center items-center cursor-pointer absolute z-50 lg:top-[50vh] h-24 w-5 shadow-lg
                bg-gradient-to-b from-[var(--color-primary)] via-[var(--color-primary-hover)] to-[var(--color-primary)]
                hover:scale-105 transition-transform
                 ${i18n.language === 'ar' ? 'right-0' : 'left-0'
                } !text-white rounded-tl-sm rounded-tr-lg rounded-bl-sm rounded-br-lg`}
              onClick={() => setShowSidePanel(!showSidePanel)}
            >
              {showSidePanel ? (
                i18n.language === 'ar' ? (
                  <FaChevronRight />
                ) : (
                  <FaChevronLeft />
                )
              ) : i18n.language === 'ar' ? (
                <FaChevronLeft />
              ) : (
                <FaChevronRight />
              )}
            </div>

            {!showCameras &&
              userType === 'enforcement' &&
              Order?.status === IN_TRANSIT && (
                <div
                  onClick={handleVideoClick}
                  role="button"
                  aria-label="Open live view"
                  tabIndex={0}
                  className="transition-all duration-300 ease-in-out rounded-lg transform hover:scale-105 flex justify-center items-center cursor-pointer absolute z-50 lg:bottom-[5px] lg:left-[10px] shadow-lg bg-gradient-to-b from-primary via-bluePrimary-100 to-primary !text-white"
                >
                  <div className="absolute z-50 lg:top-[5px] lg:right-[5px] shadow-lg !text-white bg-[var(--color-error)] px-2 py-1 rounded-md animate-pulse">
                    Live View
                  </div>
                  <img
                    src="/assets/images/live_view.jpg"
                    alt="Live View Button"
                    className="w-40 h-28 rounded-md"
                  />
                </div>
              )}

            <div className="relative h-full">
              {/* @ts-ignore */}
              <AnimatePresence initial={false}>
                {showCameras && (
                  <motion.div
                    key="camera-section"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: showSidePanel ? '36vh' : '42vh',
                      opacity: 1,
                    }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="relative overflow-hidden border-b" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-light)" }}
                  >
                    <CameraVideos
                      token={howenToken}
                      deviceId={deviceId}
                      isSidePanelOpen={showSidePanel}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* @ts-ignore */}
              <AnimatePresence initial={false}>
                {showCameras && (
                  <motion.div
                    key="camera-chevron"
                    initial={{ opacity: 0, top: 0 }}
                    animate={{
                      opacity: 1,
                      top: showSidePanel ? '36vh' : '42vh',
                    }}
                    exit={{ opacity: 0, top: 0 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className={`
                      absolute left-1/2 -translate-x-1/2 z-50 w-24 h-5 flex items-center justify-center
                      bg-gradient-to-r from-[var(--color-primary)] via-[var(--color-primary-hover)] to-[var(--color-primary)] text-white
                      rounded-tl-sm rounded-tr-sm rounded-bl-lg rounded-br-lg shadow-md
                      cursor-pointer hover:scale-105 transition-transform
                    `}
                    onClick={() => setShowCameras(false)}
                    role="button"
                    tabIndex={0}
                    aria-label="Close Live View"
                  >
                    <FaChevronUp className="w-4 h-4 text-white" />
                  </motion.div>
                )}
              </AnimatePresence>

              <div
                className={`
                  transition-all duration-500 ease-in-out
                  ${showCameras &&
                    userType === 'enforcement' &&
                    Order.status === 'intransit'
                    ? 'h-[calc(100vh-38vh)]'
                    : 'h-full'
                  }
                `}
              >
                <DashboardMap
                  defaultCenter={{
                    lat: 25.25691032409668,
                    lng: 55.289833068847656,
                  }}
                  currentLocation={
                    Order?.status === 'completed'
                      ? {
                        lat: Order.destination.coordinates[1],
                        lng: Order.destination.coordinates[0],
                      }
                      : avlData
                        ? {
                          lat: avlData?.latitude_degrees,
                          lng: avlData?.longitude_degrees,
                        }
                        : Tracker
                          ? {
                            lat: Tracker?.latitude,
                            lng: Tracker?.longitude,
                          }
                          : Order?.destination?.coordinates
                            ? {
                              lat: Order.destination.coordinates[1],
                              lng: Order.destination.coordinates[0],
                            }
                            : {
                              lat: routeDestination.latitude,
                              lng: routeDestination.longitude,
                            }
                  }
                  hideCurrentLocation={
                    Order?.status !== 'completed' &&
                    Order?.status !== 'in_transit' &&
                    !avlData &&
                    !Tracker
                  }
                  routeOrigin={
                    Order?.origin?.coordinates
                      ? {
                        latitude: Order.origin.coordinates[1],
                        longitude: Order.origin.coordinates[0],
                      }
                      : routeOrigin
                  }
                  routeDestination={
                    Order?.destination?.coordinates
                      ? {
                        latitude: Order.destination.coordinates[1],
                        longitude: Order.destination.coordinates[0],
                      }
                      : routeDestination
                  }
                  route={assignedRoute || false}
                  historyRoute={historyRoute}
                  trackerData={trackerData}
                  defaultZoom={12}
                  zoom={12}
                  isCompleted={Order?.status === COMPLETED}
                  onComplete={completeOrder}
                  events={Events}
                  order={Order}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        size="full"
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        className="p-0 max-w-none shadow-card border" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-light)", color: "var(--text-main)" }}
        backdrop="blur"
        classNames={{
          closeButton:
            'absolute left-2 top-2 bg-white/[0.06] hover:bg-white/[0.09] active:bg-white/[0.06] text-white p-2 z-50 w-10 opacity-0',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody className="p-0">
                <video
                  ref={videoRef}
                  src="/assets/videos/Murakeb.mp4" // 👈 Your video file here
                  // className="hidden" // Not visible unless in fullscreen
                  // controls
                  muted
                  playsInline
                  loop
                  preload="metadata"
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
